import { apiRoot } from '../ApiConfig'
import axios from "axios"
import React, { useState, useEffect, useMemo } from "react"
import { useNavigate } from 'react-router-dom'
import { FaCheck, FaLock, FaInfoCircle } from 'react-icons/fa'
import { useGlobal } from '../GlobalContext'
import { setGlobalTopic, setGlobalVdssSessionId } from './ActiveWords'
import { wsPreInit } from '../VoxDocuments/WebSocketPreInit'
import { ReactComponent as SearchIcon } from '../svg/search.svg'
import "./ChangeTopic.css"

const isSubscribed = (topic) => {
  if (topic.subscribed === 0 || topic.subscribed === false || topic.subscribed === "0") return false
  // getAllTopics usually only returns usable topics; treat missing flag as subscribed
  return true
}

const topicCode = (topic) => {
  const raw = topic.code || topic.language_code || topic.language || topic.lang
  if (raw) return String(raw).slice(0, 2).toUpperCase()

  const name = String(topic.name || "").toLowerCase()
  if (name.includes("french") || name.includes("français") || name.includes("francais")) return "FR"
  if (name.includes("german") || name.includes("deutsch")) return "DE"
  if (name.includes("dutch") || name.includes("nederlands")) return "NL"
  if (name.includes("english")) return "EN"
  if (name.includes("hungarian") || name.includes("magyar")) return "HU"
  if (name.includes("spanish") || name.includes("español") || name.includes("espanol")) return "ES"
  if (name.includes("italian") || name.includes("italiano")) return "IT"
  return "TP"
}

const ChangeTopic = ({ sessionId }) => {
  const navigate = useNavigate()
  const {
    selectedTopic: globalSelectedTopic,
    setSelectedTopic: setGlobalSelectedTopic,
    setGvdssSessionId,
    isWsPreinitLoading,
    setIsWsPreinitLoading,
  } = useGlobal()

  const [topics, setTopics] = useState([])
  const [pendingId, setPendingId] = useState(null)
  const [searchQuery, setSearchQuery] = useState("")

  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const response = await axios.post(apiRoot + "api/topics/getAllTopics", { sessionId })
        if (response.data.success === false) {
          if (response.data.error === "Expired session!") {
            navigate("/login")
            alert(response.data.error)
          }
        }
        setTopics(response.data.results.topics || [])
      } catch (error) {
        console.error("Failed to fetch topics:", error)
      }
    }
    fetchTopics()
  }, [sessionId, navigate])

  useEffect(() => {
    if (globalSelectedTopic?.topic_id != null) {
      setPendingId(globalSelectedTopic.topic_id)
    }
  }, [globalSelectedTopic?.topic_id])

  const filteredTopics = useMemo(() => {
    const term = searchQuery.trim().toLowerCase()
    if (!term) return topics
    return topics.filter((t) => {
      const code = topicCode(t).toLowerCase()
      return String(t.name || "").toLowerCase().includes(term) || code.includes(term)
    })
  }, [topics, searchQuery])

  const pendingTopic = topics.find((t) => String(t.topic_id) === String(pendingId))
  const dirty =
    pendingTopic &&
    isSubscribed(pendingTopic) &&
    String(pendingId) !== String(globalSelectedTopic?.topic_id ?? "")

  const choosePending = (topic) => {
    if (!isSubscribed(topic) || isWsPreinitLoading) return
    setPendingId(topic.topic_id)
  }

  const runWsPreInit = async (topic) => {
    const topicWithUrl = {
      ...topic,
      connectUrl: topic.connectUrl || `wss://${topic.url}:${topic.port}`,
    }
    if (!sessionId) {
      console.error("[ChangeTopic] no login sessionId")
      return false
    }
    if (!topicWithUrl.connectUrl || topicWithUrl.connectUrl.includes("undefined")) {
      console.error("[ChangeTopic] bad connectUrl", topicWithUrl)
      alert("Topic has no WebSocket url/port")
      return false
    }

    setIsWsPreinitLoading(true)
    try {
      await wsPreInit(
        topicWithUrl,
        sessionId,
        (sessionID) => {
          setGlobalVdssSessionId(sessionID, setGvdssSessionId)
        },
        { forceNew: true }
      )
      return true
    } catch (error) {
      console.error("[ChangeTopic] wsPreInit FAILED", error)
      alert("Server error: " + (topicWithUrl.name || "topic") + " is unreachable!")
      return false
    } finally {
      setIsWsPreinitLoading(false)
    }
  }

  const applyActiveTopic = async () => {
    if (!dirty || !pendingTopic || isWsPreinitLoading) return

    const selectedWithUrl = {
      ...pendingTopic,
      connectUrl: `wss://${pendingTopic.url}:${pendingTopic.port}`,
    }

    setGlobalTopic(selectedWithUrl)
    setGlobalSelectedTopic(selectedWithUrl)

    const ok = await runWsPreInit(selectedWithUrl)
    if (!ok) return

    try {
      const response = await axios.post(apiRoot + "api/user/selectTopicPortal", {
        sessionId,
        topicId: pendingTopic.topic_id,
      })
      if (response.data.success === false && response.data.error === "Expired session!") {
        navigate("/login")
        alert(response.data.error || "Unknown error")
      }
    } catch (error) {
      console.error("[ChangeTopic] selectTopicPortal error:", error)
    }
  }

  return (
    <div className="ctview">
      <div className="vhead">
        <div className="vhead__titles">
          <h1 className="vhead__title">Change Topic</h1>
          <p className="vhead__sub">Choose the vocabulary VoxDoc listens with</p>
        </div>
      </div>

      <div className="ctscroll">
        <div className="ctwrap">
          <div className="lsearch lsearch--sm ctwrap__search">
            <SearchIcon width={15} height={15} />
            <input
              placeholder="Search topics…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={isWsPreinitLoading}
            />
          </div>

          <div className="cthead">
            <span className="cthead__topic">Topic</span>
            <span className="cthead__ctrls">
              <span className="cthead__active">Active</span>
              <span className="cthead__sub">Subscription</span>
            </span>
          </div>

          <div className="ctcards">
            {filteredTopics.length === 0 ? (
              <div className="ctempty">No matching topics</div>
            ) : (
              filteredTopics.map((topic) => {
                const on = isSubscribed(topic)
                const isActive =
                  on && String(globalSelectedTopic?.topic_id) === String(topic.topic_id)
                const isSel = on && String(pendingId) === String(topic.topic_id)

                return (
                  <div
                    key={topic.topic_id}
                    className={"ctcard" + (isSel ? " ctcard--sel" : "") + (on ? "" : " ctcard--unsub")}
                  >
                    <span className="ctcode">{topicCode(topic)}</span>
                    <button
                      type="button"
                      className="ctcard__main"
                      onClick={() => choosePending(topic)}
                      disabled={!on || isWsPreinitLoading}
                    >
                      <span className="ctcard__name">
                        {topic.name}
                        {isActive && <span className="ctcard__badge">Active</span>}
                      </span>
                    </button>
                    <span className="ctctrls">
                      <button
                        type="button"
                        className={"ctsel" + (isSel ? " ctsel--on" : "")}
                        onClick={() => choosePending(topic)}
                        disabled={!on || isWsPreinitLoading}
                        aria-label="Set active"
                        style={on ? undefined : { visibility: "hidden" }}
                      >
                        <span className="ctsel__ring">
                          {isSel && <span className="ctsel__dot" />}
                        </span>
                      </button>
                      {on ? (
                        <span className="ctsub ctsub--on">
                          <FaCheck size={14} />
                          Subscribed
                        </span>
                      ) : (
                        <span className="ctsub">
                          <FaLock size={13} />
                          Not subscribed
                        </span>
                      )}
                    </span>
                  </div>
                )
              })
            )}
          </div>

          <div className="cttip">
            <FaInfoCircle size={16} />
            <span>
              Subscriptions are managed separately — here you choose which subscribed topic VoxDoc uses.
              Topics you aren&apos;t subscribed to are listed so you know what&apos;s available.
            </span>
          </div>
        </div>
      </div>

      <footer className="ctbar">
        <span className="ctbar__status">
          {globalSelectedTopic?.name ? (
            <span><b>{globalSelectedTopic.name}</b> is active</span>
          ) : (
            <span>No active topic</span>
          )}
        </span>
        <button
          type="button"
          className="ebtn ebtn--primary"
          onClick={applyActiveTopic}
          disabled={!dirty || isWsPreinitLoading}
        >
          <FaCheck size={17} />
          <span>Set as active topic</span>
        </button>
      </footer>

      {isWsPreinitLoading && (
        <div className="flex-column">
          <div className="modal-overlay">
            <div className="loading-indicator">Connecting to server...</div>
            <div className="spinner"></div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ChangeTopic
